<template>
  <div class="home-carousel-wrapper">
    <!-- 黑色背景区域 -->
    <div class="home-hero__bg">
      <img src="@/assets/hero-pattern.png" class="home-hero__bg-pattern" alt="background pattern">
    </div>

    <!-- 轮播导航点 -->
    <div class="home-hero__indicators">
      <div v-for="(car, i) in cars" :key="`indicator-${i}`"
        :class="['home-hero__indicator', { 'home-hero__indicator--active': currentIndex === i }]"
        @click="setCurrentIndex(i)"></div>
    </div>

    <!-- 主图区域 -->
    <div class="home-hero__main">
      <div class="home-hero__carousel">
        <transition-group name="fade">
          <div v-for="(car, i) in cars" :key="`slide-${i}`" v-show="currentIndex === i"
            class="home-hero__slide">
            <img :src="car.image" class="home-hero__img" alt="car image">
            <a class="home-hero__car-tag" :href="getCarDetailUrl(car)">
              <div class="home-hero__car-name">{{ car.model }}</div>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="12" viewBox="0 0 24 12"
                fill="none">
                <path d="M0 5.1869H15.6573V6.92024H0V5.1869Z" class="theme-fill" />
                <path d="M15.5692 0L24 5.92L15.5692 12V0Z" class="theme-fill" />
              </svg>
            </a>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'HomeCarousel',
  data () {
    return {
      cars: [],
      currentIndex: 0,
      autoplayTimer: null,
      loading: false,
      error: null,
      autoplayInterval: 5000,
    };
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      originalTopCars: 'originalTopCars',
      topCarsLoaded: 'topCarsLoaded',
      topCarsLoadTime: 'topCarsLoadTime',
      domainConfig: 'domainConfig'
    })
  },
  mounted () {
    this.fetchTopCarsIfNeeded();
    this.$root.$on('fetch-top-cars', this.fetchTopCarsIfNeeded);
  },
  beforeDestroy () {
    this.stopAutoplay();
    this.$root.$off('fetch-top-cars', this.fetchTopCarsIfNeeded);
  },
  methods: {
    fetchTopCarsIfNeeded () {
      if (this.topCarsLoaded && Array.isArray(this.originalTopCars) && this.originalTopCars.length > 0) {
        const currentTime = Date.now();
        const dataAge = currentTime - (this.topCarsLoadTime || 0);
        const maxDataAge = 15 * 60 * 1000; // 15 minutes

        if (dataAge < maxDataAge) {
          console.log('[HomeCarousel] Using cached data, age:', Math.round(dataAge / 1000 / 60), 'minutes');
          this.processTopCarsData(this.originalTopCars);
          return;
        }
      }
      this.fetchTopCars();
    },

    async fetchTopCars () {
      this.loading = true;
      this.error = null;
      try {
        const themeFilters = this.$store.getters.domainConfig?.themeFilters || {};
        const webIndex = this.$store.getters.domainConfig?.webIndex || 1;
        const params = {
          ...themeFilters,
          webIndex
        };
        const res = await this.$global.server.getRecommendedTopCars(params);
        if (res.code === 200) {
          const apiData = res.data || [];
          this.$store.commit('SET_ORIGINAL_TOP_CARS', apiData);
          this.processTopCarsData(apiData);
          console.log('[HomeCarousel] Fetched and stored top cars:', apiData.length);
        } else {
          throw new Error(res.message || '获取推荐车辆失败');
        }
      } catch (error) {
        console.error('[HomeCarousel] Error fetching top cars:', error);
        this.error = error.message || '获取推荐车辆失败';
        this.$store.commit('RESET_TOP_CARS_LOADED');
      } finally {
        this.loading = false;
      }
    },

    processTopCarsData (apiData) {
      try {
        const processedData = apiData.map(item => {
          const photos = item.photos ? item.photos.split(',') : [];
          const firstPhoto = photos.length > 0 ? photos[0] : require('@/assets/car-recommend-img.png');
          return {
            id: item.id,
            model: item.name,
            brand: item.brand,
            logo: item.logoUrl || require('@/assets/car-logo.svg'),
            year: item.year || item.marketTime?.substring(0, 4) || '-',
            category: item.purpose || '-',
            bodyType: item.type || '-',
            price: this.formatPrice(item.leastPrice, item.mostPrice) || item.price || '-',
            image: item.cover || firstPhoto,
            speedValue: item.accelerationTime ? `${item.accelerationTime}s` : '-'
          };
        });
        this.cars = this.getRandomCars(processedData, 4);
        this.startAutoplay();
      } catch (error) {
        console.error('[HomeCarousel] Error processing top cars data:', error);
      }
    },

    getRandomCars (carsArray, count) {
      if (!Array.isArray(carsArray) || carsArray.length <= count) {
        return carsArray || [];
      }
      const shuffled = [...carsArray].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, count);
    },

    setCurrentIndex (index) {
      this.currentIndex = index;
      this.restartAutoplay();
    },

    startAutoplay () {
      this.autoplayTimer = setInterval(() => {
        this.currentIndex = (this.currentIndex + 1) % this.cars.length;
      }, this.autoplayInterval);
    },

    stopAutoplay () {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },

    restartAutoplay () {
      this.stopAutoplay();
      this.startAutoplay();
    },

    formatPrice (leastPrice, mostPrice) {
      if (!leastPrice && !mostPrice) return null;
      const formatNumber = (num) => {
        if (!num) return null;
        return '$' + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };
      if (leastPrice && mostPrice) {
        return `${formatNumber(leastPrice)}-${formatNumber(mostPrice)}`;
      } else if (leastPrice) {
        return `>${formatNumber(leastPrice)}`;
      } else if (mostPrice) {
        return `<${formatNumber(mostPrice)}`;
      }
      return null;
    },

    getCarDetailUrl (car) {
      if (!car || !car.id) {
        return '#';
      }
      const currentChannel = this.$router.currentRoute.params.channel;
      const formattedName = encodeURIComponent((car.model || '').replace(/\s+/g, '-'));
      const currentQuery = this.$route.query;
      const queryString = new URLSearchParams(currentQuery).toString();
      const fullQueryString = queryString ? `?${queryString}` : '';
      if (currentChannel) {
        return `/${currentChannel}/cars/${formattedName}/${car.id}${fullQueryString}`;
      }
      return `/cars/${formattedName}/${car.id}${fullQueryString}`;
    }
  }
};
</script>

<style lang="scss">
@import '~@/css/hero.scss';

.home-hero--mobile {
	.home-hero__bg {
		border-radius: 0 0 rem(36) 0;
		height: 100%;
	}

	.home-hero__indicators {
		left: rem(16);
		top: rem(298);
		bottom: auto;
		gap: rem(8);
	}
	.home-hero__indicator {
		background: #ffffff;
	}
	.home-hero__indicator--active {
		background: var(--theme-color);
	}

	.home-hero__main {
		width: 100%;
		height: rem(210);
		top: rem(72);
		right: 0;
	}

	.home-hero__img {
		border-radius: rem(36) 0 0 0;
	}

	.home-hero__car-tag {
		right: rem(12);
		bottom: rem(12);
		background: #141414;
		border-radius: rem(12);
		padding: rem(6) rem(20);
	}
}
</style>
